import { useMemo, useState, type DragEvent } from "react";
import { Link } from "react-router";
import { api } from "../../data";
import { useAction } from "../../components/ui";
import { date } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { NeuerLead } from "./Leads";
import { OwnerKuerzel, useIch, useLeads, useStaff, VerlorenDialog, ZustaendigFilter } from "./crm-teile";
import {
  ALLE_LEADS, faelligkeit, filterLeads, heuteBerlin, leadName, NEBEN, PIPELINE, QUELLEN, quelleLabel, setStage, spalten, STUFEN, stufeLabel,
  type LeadFilter, type LeadRow, type Stufe,
} from "./crm-data";

/**
 * Pipeline (Vertrieb-Konsole, Inhaber 02.11.2026): eine Spalte je Stufe von „Neu“ bis „Gewonnen“, darunter „Später“ und
 * „Verloren“. Verschieben per Ziehen oder über die Auswahl an der Karte (auch mit Tastatur); „Verloren“ fragt nach dem Grund.
 * Alle Mitarbeiter sehen alle Leads; der Filter „Meine“ zeigt nur die eigenen.
 */
export function Pipeline() {
  const q = useLeads();
  const staff = useStaff();
  const ich = useIch();
  const [filter, setFilter] = useState<LeadFilter>({ ...ALLE_LEADS, stufe: "alle" });
  const [verloren, setVerloren] = useState<string | null>(null);
  const [ziel, setZiel] = useState<Stufe | null>(null);
  const { busy, run } = useAction();
  const heute = heuteBerlin();
  const aktive = useMemo(() => new Set((staff.data ?? []).filter((s) => s.active).map((s) => s.user_id)), [staff.data]);
  const gefiltert = useMemo(() => filterLeads(q.data ?? [], filter, ich, heute, aktive), [q.data, filter, ich, heute, aktive]);
  const nachStufe = useMemo(() => spalten(gefiltert, heute), [gefiltert, heute]);

  function verschieben(id: string, nach: Stufe) {
    const l = q.data?.find((x) => x.id === id);
    if (!l || l.stage === nach) return;
    if (nach === "verloren") { setVerloren(id); return; }
    void run(`stufe-${id}`, () => setStage(api, id, nach), `${leadName(l)}: ${stufeLabel(nach)}.`);
  }
  const ablegen = (stufe: Stufe) => ({
    onDragOver: (e: DragEvent) => { e.preventDefault(); setZiel(stufe); },
    onDragLeave: () => setZiel((z) => (z === stufe ? null : z)),
    onDrop: (e: DragEvent) => { e.preventDefault(); setZiel(null); const id = e.dataTransfer.getData("text/plain"); if (id) verschieben(id, stufe); },
  });

  const kopf = <><title>Pipeline · Konsole · CreatorOffice</title>
    <div className="pagehead"><div><h1>Pipeline</h1><p className="muted">Leads nach Stufe. Karte ziehen oder die Stufe an der Karte wählen.</p></div><NeuerLead /></div></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending) return <>{kopf}<Laden /></>;
  return (
    <>
      {kopf}
      <div className="toolbar tk-crm-filter">
        <input type="search" aria-label="Leads durchsuchen" placeholder="Firma, Kontakt, E-Mail …" value={filter.suche} onChange={(e) => setFilter({ ...filter, suche: e.target.value })} />
        <ZustaendigFilter value={filter.zustaendig} onChange={(zustaendig) => setFilter({ ...filter, zustaendig })} />
        <select aria-label="Quelle" value={filter.quelle} onChange={(e) => setFilter({ ...filter, quelle: e.target.value as LeadFilter["quelle"] })}>
          <option value="alle">Alle Quellen</option>
          {QUELLEN.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
      </div>
      <div className="tk-crm-board" aria-label="Pipeline nach Stufen">
        {PIPELINE.map((s) => (
          <Spalte key={s} stufe={s} leads={nachStufe[s]} heute={heute} ziel={ziel === s} ablegen={ablegen(s)} verschieben={verschieben} busy={!!busy} />
        ))}
      </div>
      <div className="grid two tk-crm-neben">
        {NEBEN.map((s) => (
          <Spalte key={s} stufe={s} leads={nachStufe[s]} heute={heute} ziel={ziel === s} ablegen={ablegen(s)} verschieben={verschieben} busy={!!busy} breit />
        ))}
      </div>
      <VerlorenDialog leadId={verloren} onClose={() => setVerloren(null)} />
    </>
  );
}

function Spalte({ stufe, leads, heute, ziel, ablegen, verschieben, busy, breit }: {
  stufe: Stufe; leads: LeadRow[]; heute: string; ziel: boolean; busy: boolean; breit?: boolean;
  ablegen: { onDragOver: (e: DragEvent) => void; onDragLeave: () => void; onDrop: (e: DragEvent) => void };
  verschieben: (id: string, nach: Stufe) => void;
}) {
  const staff = useStaff();
  return (
    <section className={`tk-crm-spalte${ziel ? " tk-crm-ziel" : ""}${breit ? " tk-crm-spalte-breit" : ""}`} aria-labelledby={`spalte-${stufe}`} {...ablegen}>
      <h2 id={`spalte-${stufe}`} className="tk-crm-spalte-kopf"><span>{stufeLabel(stufe)}</span><span className="tk-chip-count">{leads.length}</span></h2>
      {leads.length === 0 ? <p className="muted tk-crm-leer">Leer</p> : (
        <ul className="tk-crm-karten">
          {leads.map((l) => {
            const f = faelligkeit(l.next_step_at, heute);
            return (
              <li key={l.id} className="tk-crm-karte" draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", l.id); e.dataTransfer.effectAllowed = "move"; }}>
                <div className="tk-crm-karte-kopf">
                  <Link to={`/intern/leads/${l.id}`}><strong>{leadName(l)}</strong></Link>
                  <OwnerKuerzel staff={staff.data} id={l.owner_id} />
                </div>
                {l.contact_name && l.company && <small>{l.contact_name}</small>}
                <small className="muted">{quelleLabel(l.source)}{l.org_id ? " · Konto" : ""}</small>
                {l.next_step_at && (
                  <small className={f === "ueberfaellig" ? "tk-crm-ueberfaellig" : f === "heute" ? "tk-crm-heute" : undefined}>
                    {f === "ueberfaellig" ? "überfällig: " : f === "heute" ? "heute: " : ""}{l.next_step ?? "nächster Schritt"} · {date(l.next_step_at)}
                  </small>
                )}
                {stufe === "verloren" && l.lost_reason && <small className="muted">Grund: {l.lost_reason}</small>}
                <label className="tk-crm-karte-stufe"><span className="sr-only">Stufe von {leadName(l)}</span>
                  <select value={l.stage} disabled={busy || !!l.objected_at} onChange={(e) => verschieben(l.id, e.target.value as Stufe)}>
                    {STUFEN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
